import React, { useState, useRef } from "react";

// Pomocná funkcia na formátovanie času
function formatMinutesToTime(minutes) {
  if (!minutes) return '-';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

export default function Subtasks({
  subtasks = [],
  taskId,
  onToggle,
  onAdd,
  onDelete,
  onReorder,
  onExport,
}) {
  const [newSubtask, setNewSubtask] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [noteEditId, setNoteEditId] = useState(null);
  const [noteEditText, setNoteEditText] = useState("");
  const [quantityPromptId, setQuantityPromptId] = useState(null);
  const [quantityInput, setQuantityInput] = useState("1");
  const dragItem = useRef();
  const dragOverItem = useRef();

  // Drag & drop reorder
  function handleDragStart(idx) {
    dragItem.current = idx;
  }
  function handleDragEnter(idx) {
    dragOverItem.current = idx;
  }
  function handleDragEnd() {
    const from = dragItem.current;
    const to = dragOverItem.current;
    if (from !== undefined && to !== undefined && from !== to && onReorder) {
      const reordered = [...subtasks];
      const [moved] = reordered.splice(from, 1);
      reordered.splice(to, 0, moved);
      onReorder(reordered);
    }
    dragItem.current = undefined;
    dragOverItem.current = undefined;
  }

  // Edit subtask
  function startEdit(st) {
    setEditId(st.id);
    setEditText(st.text);
  }
  function saveEdit(st) {
    if (editText.trim() && st.text !== editText.trim() && onReorder) {
      const updated = subtasks.map(s => s.id === st.id ? { ...s, text: editText.trim() } : s);
      onReorder(updated);
    }
    setEditId(null);
    setEditText("");
  }

  // Edit note
  function startNoteEdit(st) {
    setNoteEditId(st.id);
    setNoteEditText(st.note || "");
  }
  function saveNoteEdit(st) {
    if (onReorder) {
      const updated = subtasks.map(s => s.id === st.id ? { ...s, note: noteEditText } : s);
      onReorder(updated);
    }
    setNoteEditId(null);
    setNoteEditText("");
  }

  // Export tracked time (button)
  function handleExport(st) {
    if (onExport) onExport(st);
  }

  return (
    <div style={{ margin: '1rem auto', width: '100%' }}>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {subtasks.map((st, idx) => (
          <li
            key={st.id}
            draggable={!!onReorder}
            onDragStart={() => handleDragStart(idx)}
            onDragEnter={() => handleDragEnter(idx)}
            onDragEnd={handleDragEnd}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2,
              background: dragOverItem.current === idx ? '#e3f2fd' : 'none',
              borderRadius: 4, padding: '2px 2px 2px 0',
              cursor: !!onReorder ? 'grab' : 'default',
              userSelect: 'none',
              position: 'relative'
            }}>
              {/* Drag handle vizuál */}
              {!!onReorder && (
                <span style={{
                  display: 'inline-block',
                  width: 16,
                  height: 16,
                  marginRight: 4,
                  color: '#bbb',
                  cursor: 'grab',
                  fontSize: 16,
                  lineHeight: '16px',
                  verticalAlign: 'middle',
                  userSelect: 'none'
                }} title="Presuň podúlohu">
                  &#8942;
                </span>
              )}
              <input
                type="checkbox"
                checked={!!st.completed}
                onChange={() => {
                  if (!st.completed) {
                    // pri označení ako hotové – spýtaj sa na počet
                    setQuantityPromptId(st.id);
                    setQuantityInput(st.quantity != null ? String(st.quantity) : "1");
                  } else {
                    onToggle && onToggle(st.id);
                  }
                }}
                style={{ marginRight: 4 }}
                aria-label="Označiť podúlohu ako splnenú"
              />
              {quantityPromptId === st.id ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#475569' }}>Počet ks:</span>
                  <input
                    autoFocus
                    type="number"
                    min="1"
                    value={quantityInput}
                    onChange={e => setQuantityInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        if (onReorder) {
                          const updated = subtasks.map(s => s.id === st.id ? { ...s, quantity: Number(quantityInput) || 1 } : s);
                          onReorder(updated);
                        }
                        setQuantityPromptId(null);
                        onToggle && onToggle(st.id);
                      }
                      if (e.key === 'Escape') setQuantityPromptId(null);
                    }}
                    style={{ width: 60, fontSize: 13, border: '1px solid #0ea5e9', borderRadius: 6, padding: '2px 6px', outline: 'none' }}
                  />
                  <button
                    onClick={() => {
                      if (onReorder) {
                        const updated = subtasks.map(s => s.id === st.id ? { ...s, quantity: Number(quantityInput) || 1 } : s);
                        onReorder(updated);
                      }
                      setQuantityPromptId(null);
                      onToggle && onToggle(st.id);
                    }}
                    style={{ fontSize: 12, background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 5, padding: '2px 8px', cursor: 'pointer' }}
                  >✓</button>
                  <button
                    onClick={() => setQuantityPromptId(null)}
                    style={{ fontSize: 12, background: '#f1f5f9', border: 'none', borderRadius: 5, padding: '2px 6px', cursor: 'pointer', color: '#64748b' }}
                  >✕</button>
                </span>
              ) : (
                <span style={{ textDecoration: st.completed ? 'line-through' : 'none' }}>
                  {st.text}
                  {st.quantity != null && !st.completed && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#0369a1', background: '#e0f2fe', borderRadius: 4, padding: '1px 5px' }}>{st.quantity} ks</span>
                  )}
                  {st.quantity != null && st.completed && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8', borderRadius: 4, padding: '1px 5px' }}>{st.quantity} ks</span>
                  )}
                </span>
              )}
              {/* Tlačidlo na vymazanie podúlohy */}
              {onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(st.id)}
                  style={{
                    marginLeft: 4,
                    color: '#dc3545',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '2px 7px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  title="Zmazať podúlohu"
                >✕</button>
              )}
              {/* Poznámka podúlohy */}
              <div style={{marginLeft: 24, marginTop: 2, width: '100%'}}>
                {noteEditId === st.id ? (
                  <>
                    <textarea
                      value={noteEditText}
                      onChange={e => setNoteEditText(e.target.value)}
                      style={{width: '100%', minHeight: 40, fontSize: 13, marginBottom: 4}}
                    />
                    <button onClick={() => saveNoteEdit(st)} style={{marginRight: 4}}>Uložiť poznámku</button>
                    <button onClick={() => { setNoteEditId(null); setNoteEditText(""); }}>Zrušiť</button>
                  </>
                ) : (
                  <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <span style={{fontSize: 13, color: '#888'}}>{st.note ? st.note : <i>Bez poznámky</i>}</span>
                    <button onClick={() => startNoteEdit(st)} style={{fontSize: 12}}>✎ Poznámka</button>
                  </div>
                )}
              </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
